import { Preview } from "@creatomate/preview";

import { ElementSource, Organization, Story, Template, TemplateType } from "chq";

import deleteElement from "./deleteElement";
import getDefaultSource from "./getDefaultSource";
import setCustomTemplate from "./setCustomTemplate";
import { isSubtitleText } from "../../sidebar/tabs/subtitle/utils";
import { GENERIC_ELEMENT } from "../constants";
import loadOrganizationFonts from "../loadOrganizationFonts";
import sourceToSnakeCase from "./sourceToSnakeCase";
import getMediaElements from "./getMediaElements";

const syncFonts = async (organization: Organization, preview: Preview): Promise<void> => {
  const source = preview.getSource();
  const fonts = loadOrganizationFonts(organization);
  await preview.setSource({ ...source, fonts });
};

const setTemplate = async (
  type: TemplateType,
  preview: Preview,
  organization: Organization,
  mediaType: string,
  template: Template,
  story?: Story
): Promise<void> => {
  const source = preview.getSource();
  const { elements } = source;
  const allSubtitleElements = elements.filter(
    (element: ElementSource) => isSubtitleText(element.name)
  );

  const allMediaElements = getMediaElements(mediaType, preview);
  // try to get the I=STORY, this works if a template was previously applied
  const storyElementsInTemplate = allMediaElements?.filter(
    storyElement => storyElement.source.name === GENERIC_ELEMENT.STORY
  );

  const firstMediaElement = (() => {
    if (storyElementsInTemplate?.length) return storyElementsInTemplate[0];
    return allMediaElements ? allMediaElements[0] : undefined;
  })();

  let lastEndTime = 0;
  const mediaElements = firstMediaElement
    ? allMediaElements?.filter(media => media.track === firstMediaElement.track).map(
      media => {
        const newTime = lastEndTime;
        lastEndTime = newTime + media.duration;
        return ({ ...media,
          globalTime: newTime,
          localTime: newTime
        });
      }
    ) : [];

  if (template && story) {
    await preview.setSource(sourceToSnakeCase(template?.creatomateJson), true);
    await setCustomTemplate(type, template, preview, organization, story, mediaElements);
    await syncFonts(organization, preview);
  } else {
    await Promise.all((preview.state?.elements || []).map(async element => {
      await deleteElement(preview, element.source.id);
    }));
    await preview.setSource(await getDefaultSource({
      organization,
      preview,
      story,
      mediaElements
    }));
  }

  if (allSubtitleElements.length > 0) {
    const prevFirstStory = source.elements
      .filter((element: ElementSource) => element.name === GENERIC_ELEMENT.STORY)
      ?.reduce((min: ElementSource | null, current: ElementSource) => {
        if (!min || (current.time ?? 0) < (min.time ?? 0)) return current;

        return min;
      }, null as ElementSource | null);

    const firstStory = template?.creatomateJson?.elements
      ?.filter((element: ElementSource) => element.name === GENERIC_ELEMENT.STORY)
      ?.reduce((min: ElementSource | null, current: ElementSource) => {
        if (!min || (current.time ?? 0) < (min.time ?? 0)) return current;

        return min;
      }, null as ElementSource | null);

    const minOldTrack = allSubtitleElements.reduce(
      (min: number, element: ElementSource) => Math.min(min, element.track || 0),
      allSubtitleElements[0].track
    );

    const newSource = preview.getSource();
    const newTrack = (newSource.elements.reduce(
      (max: number, el: ElementSource) => Math.max(max, el.track || 0), 0) || 0
    ) + 1;
    await preview.setSource({
      ...newSource,
      elements: [
        ...newSource.elements,
        ...allSubtitleElements.map((element: ElementSource) => ({
          ...element,
          time: (firstStory?.time ?? 0) + (element.time - (prevFirstStory?.time ?? 0)),
          track: element.track + newTrack - minOldTrack
        }))
      ]
    });
  }
};

export default setTemplate;
